import type { SupabaseClient } from '@supabase/supabase-js';
import {
  createGrid,
  type ColDef,
  type GridApi,
  type GridOptions,
  type ICellRendererParams,
  type IDatasource,
  type IGetRowsParams,
  type ValueFormatterParams,
  type ValueGetterParams,
  type ValueSetterParams,
} from 'ag-grid-community';
import { awaitJob } from '../../lib/await-job.js';
import { varioArticleUrl, varioListingUrl } from '../../lib/vario-links.js';
import { openOttoAssignDialog } from './otto-offers-article-dialog.js';
import { openBulkAssignModal, pickAssignable } from './otto-offers-bulk-assign.js';
import { openActiveStatusModal, splitForActiveStatus } from './otto-offers-active-status.js';
import { showToast, openModal, createKfDrawer, KF_ICON, KF_LOGO_GOECOMMERCE } from '@vario-connect/frontend-design';
import { httpErrorText } from '../../lib/session-expiry.js';

interface OffersCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

interface OttoStatusMessage { code: string | null; title: string | null }

/** Abschnitt „Meldungen von OTTO“ im Detail. OTTO-Code „300010 - MEDIA_…“ → Nummer klein davor.
 *  Abgelehnt ohne errors[] kommt häufig vor (live 15.10.2026: 105 von 113); dann das offen sagen. */
function ottoMessagesHtml(row: { status: string | null; otto_errors: OttoStatusMessage[] | null; otto_information: OttoStatusMessage[] | null }): string {
  const item = (m: OttoStatusMessage) => {
    const num = (m.code ?? '').split(' - ')[0]?.trim() ?? '';
    return `<div class="kv2"><div class="k mono">${escapeHtml(num || 'OTTO')}</div><div class="v">${escapeHtml(m.title ?? m.code ?? '')}</div></div>`;
  };
  const errs = row.otto_errors ?? [];
  const infos = row.otto_information ?? [];
  if (errs.length === 0 && infos.length === 0) {
    if (row.status !== 'error') return '';
    return `<div class="dsec"><div class="lab">Meldungen von OTTO</div><div class="dcols">
      <div class="kv2"><div class="k">Grund</div><div class="v">OTTO nennt für diese Variante keinen Grund. Details stehen im OTTO Partner Connect.</div></div>
    </div></div>`;
  }
  return `<div class="dsec"><div class="lab">Meldungen von OTTO</div><div class="dcols">
    ${errs.map(item).join('')}${infos.map(item).join('')}
  </div></div>`;
}

/** Zeile aus `otto.units_with_mapping` (read-only). Identität = `sku`. */
interface UnitRow {
  storefront: string;
  sku: string;
  ean: string | null;
  title: string | null;
  status: string | null;
  price_cents: number | null;
  stock: number | null;
  otto_product_link: string | null;
  processing_status: string | null;
  /** OTTO-Gründe und Hinweise aus marketplace-status (Migration 0010), je {code, title}. */
  otto_errors: OttoStatusMessage[] | null;
  otto_information: OttoStatusMessage[] | null;
  fetched_at: string | null;
  updated_at: string;
  deleted_at: string | null;
  /** From vario.article_mappings (LEFT JOIN). null = noch nicht zugewiesen. */
  vario_article_id: string | null;
  vario_article_number: string | null;
  /** Echte Vario-Artikelnummer der VARIANTE. Otto-Kind-Mappings tragen die Vater-Nummer in
   *  `vario_article_number`; diese View-Spalte (0263) liefert bei Kindern die Kind-Nummer (= sku),
   *  sonst die Mapping-Nummer. Für Anzeige/Suche der „Vario-SKU" nutzen. */
  effective_vario_article_number: string | null;
  /** Vario-Artikel-ID + Listing-ID des Kanal-Listings (View 0274) für den Verkaufskanal-Deeplink.
   *  Kind → echte Kind-aid + dessen Listing; Einzel → Artikel-aid + dessen Listing. */
  channel_vario_article_id: string | null;
  channel_vario_listing_id: string | null;
  /** Aktueller Vario-Bestand (stockAvailable). NULL = noch nie refresht oder kein Mapping. */
  vario_stock: number | null;
  vario_stock_fetched_at: string | null;
  /** From vario.article_match_suggestions (LEFT JOIN). NON-NULL bedeutet: Vario-Artikel
   *  existiert mit passender EAN/SKU, ist aber noch nicht via Mapping übernommen. Bei
   *  (vario_article_id == null && suggested_vario_article_id != null) → Zeile blau hervorheben. */
  suggested_vario_article_id: string | null;
  suggested_vario_article_number: string | null;
  suggested_match_source: string | null;
  /** Min-Bestand für die Auffüllen-Logik. NULL = Feature aus. Trigger: otto_stock ≤ min ODER vario < min. */
  min_bestand: number | null;
  /** Max-Bestand für die Auffüllen-Logik (Zielwert beim Push). NULL = Feature aus. */
  max_bestand: number | null;
  /** Ziel-Verkaufspreis (Vario-Kanal-Preis) in Cent, zuletzt über das Grid gesetzt. NULL = nie
   *  geändert. Weicht er von `price_cents` (Otto) ab → Preis-Push läuft (Zelle orange). */
  channel_price_cents: number | null;
  /** Vario-Artikelart (WITH_STOCK | WITHOUT_STOCK | SERVICE_ARTICLE). null = noch nicht erfasst.
   *  WITHOUT_STOCK/SERVICE_ARTICLE = bestandslos → „Vario"-Spalte zeigt „kein Bestand", Min/Max
   *  füllt auf Max ohne Vario-Deckel. */
  vario_product_type: string | null;
}

/** Bestandslose Vario-Artikelarten (kein Lagerbestand) - Spiegel des Workers. */
const STOCKLESS_PRODUCT_TYPES = new Set(['WITHOUT_STOCK', 'SERVICE_ARTICLE']);
function isStocklessRow(row: UnitRow | undefined): boolean {
  return !!row && row.vario_product_type !== null && STOCKLESS_PRODUCT_TYPES.has(row.vario_product_type);
}

// Bestands-Automatik-Berechnung je Zeile. MODUL-SCOPE, weil sowohl der Detail-Drawer
// (renderOffersTab) als auch die Grid-Spalten (top-level initGrid) sie brauchen.
type StockCalc =
  | { state: 'off' }
  | { state: 'pending'; min: number; max: number; kl: number }
  | { state: 'stockless'; kl: number; min: number; max: number; cMax: boolean; cMin: boolean; target: number; push: boolean }
  | { state: 'on'; kl: number; varioAvail: number; min: number; max: number; abundant: boolean; target: number; push: boolean };

function offersStockCalc(row: UnitRow): StockCalc {
  const min = row.min_bestand;
  const max = row.max_bestand;
  if (min == null || max == null) return { state: 'off' };
  const kl = row.stock ?? 0;
  if (isStocklessRow(row)) {
    // Bestandslos: Ziel = Max, kein Vario-Deckel; Auslöser nur kl>Max / kl≤Min.
    const cMax = kl > max;
    const cMin = kl <= min;
    const target = Math.max(0, max);
    return { state: 'stockless', kl, min, max, cMax, cMin, target, push: (cMax || cMin) && target !== kl };
  }
  if (row.vario_stock == null) return { state: 'pending', min, max, kl };
  // Spiegel des Workers `selectQuantityPushes` (bestandsführend):
  //  - Vario ≤ Max → Ziel = Vario (echte Verfügbarkeit spiegeln, verhindert Überverkauf).
  //  - Vario > Max (reichlich) → Hysterese-Band: Otto ≤ Min oder Otto > Max → auf Max; sonst kein Push.
  const varioAvail = Math.trunc(row.vario_stock);
  const abundant = varioAvail > max;
  let target: number;
  let push: boolean;
  if (!abundant) {
    target = Math.max(0, varioAvail);
    push = target !== kl;
  } else if (kl <= min || kl > max) {
    target = max;
    push = target !== kl;
  } else {
    target = kl;
    push = false;
  }
  return { state: 'on', kl, varioAvail, min, max, abundant, target, push };
}

// Soll die „Bestand"-Zelle orange markiert werden? Genau dann, wenn der nächste Abgleich den
// Otto-Bestand TATSÄCHLICH ändern würde (Nutzerwunsch 2026-07-14: „wenn es eine Bestands-
// veränderung geben würde, Min/Max usw."). Spiegelt den Worker-Push `selectQuantityPushes`:
//   - Min+Max gesetzt → Min/Max-Automatik (offersStockCalc.push): nur wenn Trigger greift UND
//     das Ziel vom aktuellen Otto-Bestand abweicht (Otto im Band [Min..Max] → kein Push → weiß).
//   - kein Min/Max → 1:1-Spiegel: Otto wird auf den Vario-Bestand gesetzt → Änderung bei Diff.
// Reine Vario≠Otto-Abweichung ohne Push-Wirkung (z. B. Otto 5 / Vario 15 im Band 1–7) → KEINE
// Markierung, weil sich der Otto-Bestand nicht ändert.
function ottoStockNeedsSync(row: UnitRow): boolean {
  const s = offersStockCalc(row);
  if (s.state === 'on' || s.state === 'stockless') return s.push;
  if (s.state === 'pending') return false; // Min/Max gesetzt, aber Vario-Bestand fehlt → kein Push
  // s.state === 'off' → kein Min/Max → 1:1-Spiegel: Push (Änderung), wenn Vario ≠ Otto.
  if (row.vario_stock == null) return false;
  const vario = Math.trunc(row.vario_stock);
  if (Number.isNaN(vario)) return false;
  const otto = row.stock == null ? 0 : Math.trunc(row.stock);
  return Math.max(0, vario) !== otto;
}

const GRID_BLOCK_SIZE = 200;     // Rows per server fetch - must stay ≤ PostgREST max_rows (1000 default).
const GRID_CACHE_BLOCKS = 10;    // ~2 000 rows in client memory while scrolling.

/** Snapshot of the active filter state. Passed by-reference to the datasource. */
interface FilterState {
  status: string | null;
  /** null = alle, 'mapped' = nur mit vario_article_id, 'unmapped' = nur ohne,
   *  'exists-unmapped' = ohne mapping, aber `suggested_vario_article_id` non-null
   *  (= Vario hat den Artikel via EAN/SKU bereits, nur das Mapping fehlt). */
  mapping: 'mapped' | 'unmapped' | 'exists-unmapped' | null;
  search: string;
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Aktiv',
  inactive: 'Inaktiv',
  processing: 'In Bearbeitung',
  error: 'Fehler',
  deleted: 'Gelöscht',
  unknown: 'Unbekannt',
};

interface OffersSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

/** Standalone-Slot-Renderer für den Vario-App-Tab `/#/offers`
 *  (settings.sales-channels.{appId}.tabs). Rendert die Angebotsübersicht mit
 *  eigenem Kopf (wie Bestellungen/Log) über die volle iframe-Höhe. */
export function renderOffers(ctx: OffersSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = '';
  void renderOffersTab(ctx.contentEl, { supabase: ctx.supabase, session: ctx.session, supabaseUrl: ctx.supabaseUrl }, { standalone: true });
}


export async function renderOffersTab(host: HTMLElement, ctx: OffersCtx, opts: { standalone?: boolean } = {}) {
  const standalone = !!opts.standalone;
  const appheadHtml = standalone
    ? `<header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
        <span class="kf-logo" title="Otto.de"><img src="/otto-market.png" alt="OTTO Market" style="height:24px;display:block;" /></span>
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">Otto · Angebote</div><h1>Angebotsübersicht</h1></div>
        <div class="hactions">
          <span class="hstatus"><span class="d"></span>Otto verbunden</span>
          <button type="button" class="btn btn-primary" id="kl-offers-refresh" title="Otto-Listings + Vario-Bestand neu laden">
            <span class="v-icon fa-regular fa-rotate-right" id="kl-offers-refresh-icon"></span>Aktualisieren
          </button>
        </div>
      </header>`
    : '';
  const refreshInFilterbar = standalone
    ? ''
    : `<button type="button" class="btn btn-primary" id="kl-offers-refresh" title="Otto-Listings + Vario-Bestand neu laden">
        <span class="v-icon fa-regular fa-rotate-right" id="kl-offers-refresh-icon"></span>Aktualisieren
      </button>`;
  const filterbar = `<div class="filterbar">
      <div class="select"><select id="kl-offers-status" title="Status filtern">
        <option value="">Alle Status</option>
        <option value="active">Aktiv</option>
        <option value="inactive">Inaktiv</option>
        <option value="processing">In Bearbeitung</option>
        <option value="error">Fehler</option>
        <option value="deleted">Gelöscht</option>
      </select></div>
      <div class="select"><select id="kl-offers-mapping" title="Vario-Mapping filtern">
        <option value="">Alle Mappings</option>
        <option value="unmapped">Nicht zugeordnet</option>
        <option value="exists-unmapped">In Vario vorhanden, nicht zugeordnet</option>
        <option value="mapped">Zugeordnet</option>
      </select></div>
      <div class="tbsearch">${KF_ICON.search}<input id="kl-offers-search" type="search" placeholder="EAN, SKU, Titel suchen…" autocomplete="off" /><button type="button" class="clearbtn" id="kl-offers-search-clear" aria-label="Suche leeren">${KF_ICON.close}</button></div>
      ${refreshInFilterbar}
      <div class="kl-legend-wrap">
        <button type="button" class="icon-btn" id="kl-offers-legend-btn" title="Legende anzeigen" aria-label="Legende anzeigen" aria-expanded="false">${KF_ICON.help}</button>
        <div id="kl-offers-legend-popover" class="kl-legend-popover" hidden>
          <div class="kl-legend-title">Zeilen-Farben</div>
          <ul class="kl-legend-list">
            <li><span class="kl-legend-swatch kl-legend-swatch-red"></span><span><strong>Hellrot</strong> - nicht zugeordnet</span></li>
            <li><span class="kl-legend-swatch kl-legend-swatch-blue"></span><span><strong>Hellblau</strong> - in Vario vorhanden, aber nicht zugeordnet</span></li>
            <li><span class="kl-legend-swatch kl-legend-swatch-white"></span><span><strong>Weiß</strong> - zugeordnet</span></li>
          </ul>
          <div class="kl-legend-title kl-legend-title--sub">Synchronisation</div>
          <ul class="kl-legend-bullets">
            <li>Bestände werden regelmäßig abgeglichen</li>
            <li>Manueller Sync jederzeit über „Aktualisieren"</li>
          </ul>
        </div>
      </div>
    </div>`;
  const gridWrapStyle = standalone ? 'flex:1;min-height:0;' : 'height:min(74vh,900px);min-height:480px;flex:none;';
  // Bulk-Toolbar: leerer Container, `.bulkbar:empty { display:none }` hält ihn ohne Auswahl flach.
  const toolbar = `<div id="kl-offers-toolbar"></div>`;
  const card = `<div class="card"><div class="grid-wrap" style="${gridWrapStyle}"><div id="kl-grid" class="ag-theme-vario ag-theme-quartz" style="height:100%;width:100%;"></div></div></div>`;
  const body = `${filterbar}${toolbar}${card}`;
  host.innerHTML = standalone
    ? `<div class="kf-page" style="height:100%;display:flex;flex-direction:column;"><main class="main">${appheadHtml}${body}</main></div>`
    : `<div class="kf-page" style="height:auto;display:block;">${body}</div>`;

  const root = host.querySelector<HTMLElement>('.kf-page')!;
  const drawer = createKfDrawer(root);
  if (standalone) {
    const fit = () => { root.style.height = window.innerHeight + 'px'; };
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    fit();
  }

  const filters: FilterState = {
    status: null,
    mapping: null,
    search: '',
  };
  let gridApi: GridApi<UnitRow> | null = null;

  // Bulk-Toolbar (Zeilenauswahl → Min/Max-Bestand). Wird bei jedem Selektions-Wechsel
  // neu gerendert; bei 0 markierten Zeilen bleibt der Container leer (`.bulkbar:empty`).
  const renderBulkToolbar = () => renderToolbar(host, ctx, gridApi, () => reloadRows());

  gridApi = initGrid(host, ctx, filters, (row) => openOffersDrawer(row), renderBulkToolbar, (row) => openAssignDialog(row));
  bindFilters();
  bindSyncButton();

  // Kein On-Open-Auto-Refresh mehr (früher: Vario-Kanal-Preis beim Öffnen nachziehen): Vario-Bestand
  // und -Preis werden laufend automatisch in die DB gespiegelt (Webhook-Direct-Write + Cron-Refresh),
  // das Grid zeigt beim Öffnen also bereits den aktuellen Stand — ein Spinner/Refresh beim Öffnen ist
  // überflüssig. Der volle Sync bleibt manuell über „Aktualisieren" (bindSyncButton) verfügbar.

  // -------------------------------------------------------------------------
  // Detail-Drawer (read-only): Identifikation, Preis, Bestand (Otto vs. Vario),
  // Status + processing_status, Otto-Produktlink + Vario-Artikel-Link.
  // -------------------------------------------------------------------------
  function stockSectionHtml(row: UnitRow): string {
    const s = offersStockCalc(row);
    const LAB = '<div class="lab">Bestands-Automatik · Min / Max</div>';
    if (s.state === 'off') {
      return `<div class="dsec">${LAB}<div class="calc-off"><b>Automatik aus.</b><span>Min &amp; Max sind nicht beide gesetzt - für diese Zeile findet keine automatische Bestands-Anpassung statt. Beide Schwellen setzen, um sie zu aktivieren.</span></div></div>`;
    }
    if (s.state === 'pending') {
      return `<div class="dsec">${LAB}<div class="calc-off"><b>Wartet auf Vario-Bestand.</b><span>Min ${s.min} / Max ${s.max} sind gesetzt, aber der Vario-Bestand wurde noch nicht geladen. Die Automatik greift ab dem nächsten Sync.</span></div></div>`;
    }
    const cond = (on: boolean, txt: string, calc: string) =>
      `<div class="cc ${on ? 'on' : ''}"><span class="dot"></span>${txt}<i>${calc}</i></div>`;
    const condsHtml =
      s.state === 'stockless'
        ? cond(s.cMax, 'kl &gt; Max', `${s.kl} &gt; ${s.max}`) + cond(s.cMin, 'kl ≤ Min', `${s.kl} ≤ ${s.min}`)
        : cond(!s.abundant, 'Vario ≤ Max → Ziel = Vario', `${s.varioAvail} ≤ ${s.max}`) +
          cond(s.abundant && s.kl <= s.min, 'reichlich &amp; Otto ≤ Min → auf Max', `${s.kl} ≤ ${s.min}`) +
          cond(s.abundant && s.kl > s.max, 'reichlich &amp; Otto &gt; Max → auf Max', `${s.kl} &gt; ${s.max}`);
    const valsHtml =
      s.state === 'stockless'
        ? `<div class="cv"><span>Otto (kl)</span><b>${s.kl}</b></div><div class="cv"><span>Vario</span><b style="font-size:13px">kein Bestand</b></div><div class="cv"><span>Min · Auslöser</span><b>${s.min}</b></div><div class="cv"><span>Max · Ziel</span><b>${s.max}</b></div>`
        : `<div class="cv"><span>Otto (kl)</span><b>${s.kl}</b></div><div class="cv"><span>Vario verfügbar</span><b>${s.varioAvail}</b></div><div class="cv"><span>Min · Nachfüll-Auslöser</span><b>${s.min}</b></div><div class="cv"><span>Max · Deckel</span><b>${s.max}</b></div>`;
    const targetLabel = s.state === 'stockless' ? 'Zielwert = Max (bestandslos, kein Vario-Deckel)' : 'Zielwert = Vario (≤ Max) bzw. Max (Vario &gt; Max &amp; Otto ≤ Min oder Otto &gt; Max)';
    const verdict = s.push
      ? `<div class="calc-verdict push"><span class="vd"></span>Push steht an - Otto-Bestand wird auf <b style="margin:0 3px">${s.target}</b> gesetzt</div>`
      : `<div class="calc-verdict nopush"><span class="vd"></span>Kein Push nötig - Bestand bleibt ${s.kl}</div>`;
    const note =
      s.state === 'stockless'
        ? 'Bestandsloser Artikel: <b>kein Vario-Bestand</b> → Ziel ist <b>Max</b>, ohne Vario-Deckel. Auslöser: Otto über Max oder auf/unter Min.'
        : 'Bis <b>Max</b> spiegelt Otto den <b>Vario-Bestand</b> (kein Überverkauf). Ist Vario <b>über Max</b>, bleibt Otto im Band, wird bei <b>Otto ≤ Min</b> wieder auf <b>Max</b> aufgefüllt und bei <b>Otto über Max</b> auf <b>Max</b> gedeckelt.';
    return `<div class="dsec">${LAB}<div class="calc"><div class="calc-vals">${valsHtml}</div><div class="calc-cond">${condsHtml}</div><div class="calc-target"><span>${targetLabel}</span><b>${s.target}</b></div>${verdict}</div><div class="calc-note">${note} Push läuft im Worker (alle 10 Min / manueller Sync). Der Schalter „Keine Lagerbestand-Änderungen senden" unterdrückt den Push - diese Markierung ignoriert ihn bewusst.</div></div>`;
  }

  function statusPillHtml(row: UnitRow): string {
    if (row.deleted_at) return `<span class="st st-inaktiv"><span class="pd"></span>Gelöscht</span>`;
    const status = row.status ?? 'unknown';
    const cls = status === 'active' ? 'st-aktiv' : status === 'processing' ? 'st-paus' : 'st-inaktiv';
    return `<span class="st ${cls}"><span class="pd"></span>${STATUS_LABEL[status] ?? status}</span>`;
  }

  function buildOffersDrawerHtml(row: UnitRow): string {
    const cur = (cents: number | null) =>
      new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format((cents ?? 0) / 100);
    const varioCell = row.vario_article_id != null
      ? (isStocklessRow(row) ? 'kein Bestand' : row.vario_stock != null ? String(row.vario_stock) : '…')
      : '—';
    const ottoUrl = row.otto_product_link;
    // Verkaufskanal-Deeplink (Kanal-Listing); Fallback auf Artikel-Dashboard, falls Listing-ID fehlt.
    const varioUrl = row.vario_article_id
      ? (varioListingUrl(row.channel_vario_article_id, row.channel_vario_listing_id) ?? varioArticleUrl(row.vario_article_id))
      : null;
    const footBtn = (url: string | null, label: string) =>
      url
        ? `<a class="btn btn-ghost" href="${escapeHtml(url)}" target="_blank" rel="noopener">${KF_ICON.external}${label}</a>`
        : `<button type="button" class="btn btn-ghost" disabled>${KF_ICON.external}${label}</button>`;
    return `
      <div class="dh">
        <div><div class="dt">Angebot</div><div class="doid">${escapeHtml(row.sku)}</div></div>
        <span style="margin-left:4px">${statusPillHtml(row)}</span>
        <button type="button" class="dclose" data-kf-drawer-close aria-label="Schließen">${KF_ICON.close}</button>
      </div>
      <div class="db">
        <div class="dtitle">${escapeHtml(row.title ?? row.ean ?? '—')}</div>
        <div class="dsec"><div class="lab">Identifikation</div><div class="dcols">
          <div class="kv2"><div class="k">SKU</div><div class="v mono">${escapeHtml(row.sku)}</div></div>
          <div class="kv2"><div class="k">EAN</div><div class="v mono">${escapeHtml(row.ean ?? '—')}</div></div>
          <div class="kv2"><div class="k">Vario-SKU</div><div class="v mono ${row.effective_vario_article_number ? '' : 'amber'}">${row.effective_vario_article_number ? escapeHtml(row.effective_vario_article_number) : 'nicht zugeordnet'}</div></div>
        </div></div>
        <div class="dsec"><div class="lab">Preis</div><div class="dcols">
          <div class="kv2"><div class="k">Preis</div><div class="v">${cur(row.price_cents)}</div></div>
        </div></div>
        <div class="dsec"><div class="lab">Bestand</div><div class="dcols">
          <div class="kv2"><div class="k">Otto</div><div class="v">${row.stock ?? 0}</div></div>
          <div class="kv2"><div class="k">Vario</div><div class="v">${escapeHtml(varioCell)}</div></div>
        </div></div>
        ${stockSectionHtml(row)}
        <div class="dsec"><div class="lab">Status &amp; Sync</div><div class="dcols">
          <div class="kv2"><div class="k">Status</div><div class="v">${STATUS_LABEL[row.status ?? 'unknown'] ?? escapeHtml(row.status ?? '—')}</div></div>
          <div class="kv2"><div class="k">Verarbeitung</div><div class="v">${escapeHtml(row.processing_status ?? '—')}</div></div>
          <div class="kv2"><div class="k">Aktualisiert</div><div class="v">${escapeHtml(formatRelative(row.fetched_at))}</div></div>
        </div></div>
        ${ottoMessagesHtml(row)}
      </div>
      <div class="dfoot off-dfoot">
        ${footBtn(ottoUrl, 'Otto-Produkt')}
        ${footBtn(varioUrl, 'Verkaufskanal')}
      </div>`;
  }

  function openOffersDrawer(row: UnitRow): void {
    drawer.open({ html: buildOffersDrawerHtml(row) });
  }

  // Otto-Angebot einem bestehenden Vario-Artikel zuordnen (Vorschlag oder freie Suche) bzw.
  // Zuordnung aufheben. Kein Artikel-Anlegen. Nach Erfolg Grid neu laden.
  function openAssignDialog(row: UnitRow): void {
    openOttoAssignDialog({
      supabase: ctx.supabase,
      supabaseUrl: ctx.supabaseUrl,
      session: { token: ctx.session.token },
      unit: {
        sku: row.sku,
        ean: row.ean,
        title: row.title,
        varioArticleId: row.vario_article_id,
        varioArticleNumber: row.effective_vario_article_number ?? row.vario_article_number,
        suggestedVarioArticleId: row.suggested_vario_article_id,
        suggestedVarioArticleNumber: row.suggested_vario_article_number,
        priceCents: row.price_cents,
      },
      onDone: () => reloadRows(),
    });
  }

  function bindFilters() {
    const search = host.querySelector<HTMLInputElement>('#kl-offers-search')!;
    const clearBtn = host.querySelector<HTMLButtonElement>('#kl-offers-search-clear')!;
    const updateClearVisibility = () => {
      clearBtn.classList.toggle('show', search.value.length > 0);
    };
    updateClearVisibility();
    let debounce: number | null = null;
    search.addEventListener('input', () => {
      updateClearVisibility();
      if (debounce !== null) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => {
        filters.search = search.value;
        void reloadRows();
      }, 300) as unknown as number;
    });
    clearBtn.addEventListener('click', () => {
      search.value = '';
      updateClearVisibility();
      filters.search = '';
      void reloadRows();
      search.focus();
    });
    host.querySelector<HTMLSelectElement>('#kl-offers-status')!.addEventListener('change', (e) => {
      filters.status = (e.target as HTMLSelectElement).value || null;
      void reloadRows();
    });
    host.querySelector<HTMLSelectElement>('#kl-offers-mapping')!.addEventListener('change', (e) => {
      const v = (e.target as HTMLSelectElement).value;
      filters.mapping = v === 'mapped' || v === 'unmapped' || v === 'exists-unmapped' ? v : null;
      void reloadRows();
    });
    bindLegendPopover();
  }

  function bindLegendPopover() {
    const btn = host.querySelector<HTMLButtonElement>('#kl-offers-legend-btn');
    const pop = host.querySelector<HTMLDivElement>('#kl-offers-legend-popover');
    if (!btn || !pop) return;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = !pop.hidden;
      pop.hidden = isOpen;
      btn.setAttribute('aria-expanded', String(!isOpen));
    });
    document.addEventListener('click', (e) => {
      if (pop.hidden) return;
      const target = e.target as Node;
      if (!pop.contains(target) && !btn.contains(target)) {
        pop.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
      }
    });
  }

  function reloadRows() {
    if (!gridApi) return;
    // Soft-Refresh: Blöcke im Hintergrund neu holen, OHNE das Grid zu leeren → kein weißer Blitzer,
    // Scrollposition bleibt. `purgeInfiniteCache` würde die Zeilen kurz entfernen (sichtbares
    // Flackern, v.a. bei dicht aufeinanderfolgenden Reloads). Next-Tick, weil AG Grid während des
    // initialen Renders keine Cache-Invalidierung erlaubt.
    setTimeout(() => gridApi?.refreshInfiniteCache(), 0);
  }

  // Reihenfolge der Sync-Schritte (jeder = eine RPC, die eine Job-uuid liefert). Wird im Fortschritts-
  // Modal nacheinander abgearbeitet + angezeigt: Otto-Angebote neu laden → Bestand pushen → Preise
  // pushen. Die früheren „Vario-Bestand laden"/„Vario-Preis laden"-Schritte sind entfernt: Vario-
  // Bestand und -Preis werden ohnehin laufend automatisch synchronisiert (Webhook-Direct-Write +
  // Cron-Refresh), ein manuelles Nachladen ist redundant.
  const SYNC_STEPS: Array<{ key: string; label: string; rpc: string }> = [
    { key: 'refresh', label: 'Otto-Angebote aktualisieren', rpc: 'otto_enqueue_storefront_refresh' },
    { key: 'pushqty', label: 'Bestand an Otto senden', rpc: 'otto_enqueue_push_quantities' },
    { key: 'pushprice', label: 'Preise an Otto senden', rpc: 'otto_enqueue_push_prices' },
  ];

  function bindSyncButton() {
    const btn = host.querySelector<HTMLButtonElement>('#kl-offers-refresh');
    const icon = host.querySelector<HTMLElement>('#kl-offers-refresh-icon');
    if (!btn || !icon) return;
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';

      const rowsHtml = SYNC_STEPS.map((s) =>
        `<div id="otto-sync-${s.key}" style="display:flex;align-items:center;gap:10px;padding:9px 2px;">
           <span class="v-icon fa-regular fa-circle" style="color:var(--v-fg-subtle,#9aa4ad);width:18px;text-align:center;"></span>
           <span>${s.label}</span>
         </div>`).join('');
      const modal = openModal({
        title: 'Aktualisieren',
        icon: 'fa-rotate-right',
        width: 460,
        dismissible: false,
        body: `<div>${rowsHtml}</div>`,
        footer: `<button type="button" class="v-button v-button--primary" data-modal-cancel disabled id="otto-sync-close">Bitte warten …</button>`,
      });
      const setStep = (key: string, state: 'running' | 'done' | 'error') => {
        const el = modal.el.querySelector<HTMLElement>(`#otto-sync-${key} .v-icon`);
        if (!el) return;
        if (state === 'running') { el.className = 'v-icon fa-regular fa-spinner-third fa-spin'; el.style.color = ''; }
        else if (state === 'done') { el.className = 'v-icon fa-solid fa-circle-check'; el.style.color = 'var(--v-positive,#84B933)'; }
        else { el.className = 'v-icon fa-solid fa-circle-xmark'; el.style.color = 'var(--v-negative,#c84a4a)'; }
      };

      let hadError = false;
      for (const s of SYNC_STEPS) {
        setStep(s.key, 'running');
        try {
          const { data, error } = await ctx.supabase.rpc(s.rpc, { _storefront: 'otto' });
          if (error) throw new Error(error.message);
          const jobId = typeof data === 'string' ? data : (Array.isArray(data) ? (data[0] as string) : (data as { job_id?: string } | null)?.job_id);
          if (jobId) await awaitJob(ctx.supabase, jobId, { timeoutMs: 5 * 60_000, pollIntervalMs: 2500 });
          setStep(s.key, 'done');
        } catch (e) {
          hadError = true;
          setStep(s.key, 'error');
          console.warn(`[offers] sync step ${s.key} failed`, e instanceof Error ? e.message : e);
        }
      }

      reloadRows();
      const closeBtn = modal.el.querySelector<HTMLButtonElement>('#otto-sync-close');
      if (closeBtn) {
        closeBtn.disabled = false;
        closeBtn.textContent = hadError ? 'Mit Fehlern abgeschlossen - Schließen' : 'Fertig - Schließen';
      }
      btn.disabled = false;
      icon.className = 'v-icon fa-regular fa-rotate-right';
    });
  }
}

// ---------------------------------------------------------------------------
// Bulk-Toolbar (Zeilenauswahl → Min/Max-Bestand)
// ---------------------------------------------------------------------------

/**
 * Rendert die schmale Bulk-Leiste über der Tabelle. Sichtbar (nicht leer) nur, solange
 * mindestens eine Zeile markiert ist - `.bulkbar:empty { display:none }` hält den Container
 * sonst flach. Zeigt die Anzahl markierter Angebote + einen „Min/Max-Bestand"-Button.
 */
function renderToolbar(
  host: HTMLElement,
  ctx: OffersCtx,
  gridApi: GridApi<UnitRow> | null,
  onReload: () => void,
): void {
  const el = host.querySelector<HTMLDivElement>('#kl-offers-toolbar');
  if (!el) return;
  const selected = gridApi?.getSelectedNodes() ?? [];
  const selCount = selected.length;
  // „Markierte Vario-Artikel zuordnen“ nur, wenn mindestens eine Markierung einen Vorschlag hat
  // und noch nicht zugeordnet ist (Filter „In Vario vorhanden, nicht zugeordnet“).
  const assignableCount = pickAssignable(selected.map((n) => n.data).filter((d): d is UnitRow => Boolean(d))).length;
  const activeSplit = splitForActiveStatus(selected.map((n) => n.data).filter((d): d is UnitRow => Boolean(d)));
  el.innerHTML = selCount === 0 ? '' : `
    <div class="bulkbar">
      <span class="selinfo"><span class="pd"></span>${selCount} markiert</span>
      ${assignableCount > 0 ? `<button type="button" class="bulk-btn" data-action="bulk-assign"
        title="Die markierten Angebote dem jeweils vorgeschlagenen VARIO-Artikel zuordnen"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/></svg>Markierte Vario-Artikel zuordnen (${assignableCount})</button>` : ''}
      <button type="button" class="bulk-btn" data-action="bulk-min-max"
        title="Min-/Max-Bestand für die markierten (zugeordneten) Angebote setzen"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7h11M19 7h1M4 17h1M9 17h11"/><circle cx="17" cy="7" r="2"/><circle cx="7" cy="17" r="2"/></svg>Min/Max-Bestand</button>
      ${activeSplit.deactivate.length > 0 ? `<button type="button" class="bulk-btn" data-action="bulk-deactivate"
        title="Die markierten Angebote auf otto.de offline nehmen"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3l18 18"/><path d="M10.6 5.1A10 10 0 0 1 12 5c5 0 9 4.5 10 7a12 12 0 0 1-3.2 4.2M6.1 6.1A12 12 0 0 0 2 12c1 2.5 5 7 10 7a9.6 9.6 0 0 0 4.4-1.1"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/></svg>Bei OTTO deaktivieren (${activeSplit.deactivate.length})</button>` : ''}
      ${activeSplit.activate.length > 0 ? `<button type="button" class="bulk-btn" data-action="bulk-activate"
        title="Die markierten inaktiven Angebote auf otto.de wieder sichtbar machen"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 12c1-2.5 5-7 10-7s9 4.5 10 7c-1 2.5-5 7-10 7S3 14.5 2 12z"/><circle cx="12" cy="12" r="3"/></svg>Bei OTTO aktivieren (${activeSplit.activate.length})</button>` : ''}
    </div>
  `;
  el.querySelector<HTMLButtonElement>('[data-action="bulk-assign"]')?.addEventListener('click', () => {
    if (!gridApi) return;
    const rows = gridApi.getSelectedNodes().map((n) => n.data).filter((d): d is UnitRow => Boolean(d));
    openBulkAssignModal(ctx, rows, () => { gridApi.deselectAll(); onReload(); });
  });
  el.querySelector<HTMLButtonElement>('[data-action="bulk-deactivate"]')?.addEventListener('click', () => {
    openActiveStatusModal(ctx, activeSplit.deactivate, false, () => { gridApi?.deselectAll(); onReload(); });
  });
  el.querySelector<HTMLButtonElement>('[data-action="bulk-activate"]')?.addEventListener('click', () => {
    openActiveStatusModal(ctx, activeSplit.activate, true, () => { gridApi?.deselectAll(); onReload(); });
  });
  el.querySelector<HTMLButtonElement>('[data-action="bulk-min-max"]')?.addEventListener('click', () => {
    if (!gridApi) return;
    const rows = gridApi.getSelectedNodes().map((n) => n.data).filter((d): d is UnitRow => Boolean(d));
    void openMinMaxBulkModal(ctx, rows, gridApi, onReload);
  });
}

/**
 * Bulk-Setter für Min-/Max-Bestand der markierten Angebote. Ein Modal mit zwei
 * Zahlenfeldern (Min, Max) - leeres Feld = diese Spalte unverändert lassen. Min/Max
 * sind reine Vario-Settings (vario.article_mappings) und existieren NUR für ZUGEORDNETE
 * Listings; nicht zugeordnete Markierungen haben kein Mapping und werden übersprungen.
 * KEIN direkter Otto-Push - der Bestands-Sync im Worker wertet die Werte aus.
 */
function openMinMaxBulkModal(
  ctx: OffersCtx,
  rows: UnitRow[],
  gridApi: GridApi<UnitRow> | null,
  onReload: () => void,
): Promise<void> {
  return new Promise((resolve) => {
    const total = rows.length;
    const mapped = rows.filter((r) => r.vario_article_id != null);
    const skipped = total - mapped.length;

    const body = `
      <div class="kl-modal-meta">
        <strong>${total} ${total === 1 ? 'Angebot' : 'Angebote'}</strong>
        <div class="text-grey">${mapped.length} zugeordnet${skipped > 0 ? ` · ${skipped} nicht zugeordnet (werden übersprungen)` : ''}</div>
      </div>
      <div class="kl-form-row">
        <label for="kl-mm-min">Min-Bestand</label>
        <div class="v-input"><input id="kl-mm-min" type="number" min="0" step="1" inputmode="numeric" placeholder="leer = unverändert" /></div>
      </div>
      <div class="kl-form-row">
        <label for="kl-mm-max">Max-Bestand</label>
        <div class="v-input"><input id="kl-mm-max" type="number" min="0" step="1" inputmode="numeric" placeholder="leer = unverändert" /></div>
      </div>
      <div class="kl-form-hint">Leeres Feld bleibt unverändert. Min/Max sind Vario-Einstellungen (kein direkter Otto-Push) - der Bestands-Sync wertet sie aus.</div>
      <div class="kl-modal-error" id="kl-mm-error" hidden></div>
    `;
    const footer = `
      <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
      <button type="button" class="v-button v-button--primary" data-action="apply" disabled>
        <span class="v-icon fa-regular fa-check"></span>Übernehmen
      </button>
    `;

    let settled = false;
    const finish = () => { if (settled) return; settled = true; resolve(); };

    const modal = openModal({
      title: 'Min/Max-Bestand',
      icon: 'fa-layer-group',
      width: 460,
      body,
      footer,
      onClose: finish,
    });

    const minInp = modal.el.querySelector<HTMLInputElement>('#kl-mm-min')!;
    const maxInp = modal.el.querySelector<HTMLInputElement>('#kl-mm-max')!;
    const errEl = modal.el.querySelector<HTMLDivElement>('#kl-mm-error')!;
    const applyBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="apply"]')!;

    // '' → null (Spalte lassen); gültige Ganzzahl ≥ 0 → Zahl; sonst undefined (ungültig).
    const parse = (v: string): number | null | undefined => {
      const t = v.trim();
      if (t === '') return null;
      const n = Number.parseInt(t, 10);
      if (Number.isNaN(n) || n < 0) return undefined;
      return n;
    };

    const validate = () => {
      const min = parse(minInp.value);
      const max = parse(maxInp.value);
      let error: string | null = null;
      if (mapped.length === 0) {
        error = 'Keines der markierten Angebote ist zugeordnet - Min/Max gibt es nur für zugeordnete Listings.';
      } else if (min === undefined || max === undefined) {
        error = 'Bitte ganze Zahlen ≥ 0 eingeben.';
      } else if (min !== null && max !== null && min > max) {
        error = `Min (${min}) darf nicht größer als Max (${max}) sein.`;
      }
      const valuesOk = min !== undefined && max !== undefined && !(min === null && max === null);
      applyBtn.disabled = !(error === null && valuesOk);
      errEl.hidden = error === null;
      if (error) errEl.textContent = error;
    };
    minInp.addEventListener('input', validate);
    maxInp.addEventListener('input', validate);
    validate(); // Initialzustand (zeigt z.B. sofort den „nichts zugeordnet"-Hinweis)

    applyBtn.addEventListener('click', async () => {
      const min = parse(minInp.value);
      const max = parse(maxInp.value);
      if (min === undefined || max === undefined || (min === null && max === null)) return;
      applyBtn.disabled = true;
      applyBtn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span>Übernehme …';
      const updated = await applyMinMaxBulk(ctx, mapped, min, max);
      gridApi?.deselectAll();
      onReload();
      if (updated > 0) {
        const parts: string[] = [];
        if (min !== null) parts.push(`Min ${min}`);
        if (max !== null) parts.push(`Max ${max}`);
        showToast(
          `${parts.join(' · ')} für ${updated} ${updated === 1 ? 'Angebot' : 'Angebote'} gesetzt${skipped > 0 ? ` · ${skipped} übersprungen` : ''}.`,
          'success',
        );
      }
      modal.close();
    });

    setTimeout(() => minInp.focus(), 50);
  });
}

/**
 * Schreibt Min/Max der (gemappten) Rows per Storefront-Gruppe über die Bulk-RPC
 * `otto_bulk_set_listing_min_max` (COALESCE-Semantik: null = Spalte unverändert).
 * Gibt die Gesamtzahl aktualisierter Mappings zurück.
 */
async function applyMinMaxBulk(
  ctx: OffersCtx,
  rows: UnitRow[],
  min: number | null,
  max: number | null,
): Promise<number> {
  const byStorefront = new Map<string, string[]>();
  for (const r of rows) {
    const arr = byStorefront.get(r.storefront) ?? [];
    arr.push(r.sku);
    byStorefront.set(r.storefront, arr);
  }
  let updated = 0;
  try {
    for (const [storefront, skus] of byStorefront) {
      const { data, error } = await ctx.supabase.rpc('otto_bulk_set_listing_min_max', {
        _storefront: storefront,
        _skus: skus,
        _min: min,
        _max: max,
      });
      if (error) throw new Error(error.message);
      updated += typeof data === 'number' ? data : Number(data ?? 0);
    }
  } catch (e) {
    showToast(`Min/Max konnte nicht gesetzt werden: ${(e as Error).message}`, 'error');
  }
  return updated;
}

// ---------------------------------------------------------------------------
// AG Grid setup
// ---------------------------------------------------------------------------

/**
 * Setter für Min/Max-Bestand-Spalten - kein pending-Pattern, die Persistierung erfolgt direkt per
 * RPC `otto_set_listing_min_max` in `vario.article_mappings`. Bei Fehler wird der lokale Wert
 * revertet. Die geänderte Grenze wirkt beim nächsten 10-Min-Push-Cron (bzw. sofort über den
 * „Aktualisieren"-Button) - es gibt keinen event-getriebenen Direkt-Push mehr.
 *
 * Leere Eingabe (NULL): „Feature aus" - sendet null an die RPC. Negative Werte werden bereits
 * client-side abgefangen, die RPC validiert nochmal als Defense-in-Depth.
 */
function makeMinMaxSetter(
  ctx: OffersCtx,
  which: 'min' | 'max',
  onChange: () => void,
) {
  return (params: ValueSetterParams<UnitRow>): boolean => {
    if (!params.data) return false;
    const row = params.data;
    const rowField = which === 'min' ? 'min_bestand' : 'max_bestand';
    const otherField = which === 'min' ? 'max_bestand' : 'min_bestand';

    // Wert parsen: leere Eingabe → null. Zahl → Integer ≥ 0. NaN → abbrechen.
    let parsed: number | null;
    if (params.newValue === null || params.newValue === undefined || params.newValue === '') {
      parsed = null;
    } else {
      const n = typeof params.newValue === 'number' ? params.newValue : Number.parseInt(String(params.newValue), 10);
      if (Number.isNaN(n) || n < 0) return false;
      parsed = Math.trunc(n);
    }

    const oldValue = (row as unknown as Record<string, unknown>)[rowField] as number | null;
    if (parsed === oldValue) return false;

    // Optimistisch lokal updaten, damit AG-Grid die Zelle sofort rendert.
    (row as unknown as Record<string, unknown>)[rowField] = parsed;
    onChange();
    if (params.node) params.api.refreshCells({ rowNodes: [params.node], force: true });

    // RPC im Hintergrund, mit beiden Werten (RPC schreibt UPDATE auf beide Spalten).
    const otherValue = (row as unknown as Record<string, unknown>)[otherField] as number | null;
    const minVal = which === 'min' ? parsed : otherValue;
    const maxVal = which === 'max' ? parsed : otherValue;

    void ctx.supabase
      .rpc('otto_set_listing_min_max', {
        _storefront: row.storefront,
        _sku: row.sku,
        _min: minVal,
        _max: maxVal,
      })
      .then((result: { error: { message: string } | null }) => {
        if (result.error) {
          // Revert + User-Hinweis. onChange() ist bei Otto ein No-Op (kein Pending-Toolbar),
          // daher die Zelle explizit neu rendern, sonst bleibt der falsche Eingabewert stehen.
          (row as unknown as Record<string, unknown>)[rowField] = oldValue;
          onChange();
          if (params.node) params.api.refreshCells({ rowNodes: [params.node], force: true });
          showToast(`Konnte ${which === 'min' ? 'Min' : 'Max'}-Bestand nicht speichern: ${result.error.message}`, 'error');
          return;
        }
      });

    return true;
  };
}

function initGrid(
  host: HTMLElement,
  ctx: OffersCtx,
  filters: FilterState,
  onRowOpen: (row: UnitRow) => void,
  onSelectionChanged: () => void,
  onAssign: (row: UnitRow) => void,
): GridApi<UnitRow> {
  const eGrid = host.querySelector<HTMLDivElement>('#kl-grid')!;

  const cols: ColDef<UnitRow>[] = [
    {
      // Checkbox-Auswahlspalte (per-Row, KEIN headerCheckboxSelection - beim Infinite Row
      // Model unzuverlässig). suppressRowClickSelection lässt den Row-Klick weiterhin den
      // Drawer öffnen; nur die Checkbox togglet die Auswahl.
      headerName: '',
      colId: 'kl-select',
      checkboxSelection: true,
      width: 44,
      maxWidth: 44,
      minWidth: 44,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      headerClass: 'kl-selection-cell',
      cellClass: 'kl-selection-cell',
      cellStyle: { padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    },
    {
      field: 'sku',
      headerName: 'SKU',
      width: 170,
      minWidth: 170,
      // Default-Sortierung beim Tab-Öffnen.
      sort: 'desc',
      cellStyle: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '11px' },
      cellClass: 'kl-ell-cell',
      tooltipField: 'sku',
    },
    {
      // Echte Vario-Artikelnummer der Variante (effective_*): Otto-Kind-Mappings tragen die
      // Vater-Nummer in vario_article_number → hier die Kind-Nummer zeigen (View 0263).
      field: 'effective_vario_article_number',
      headerName: 'Vario-SKU',
      headerTooltip: 'Artikelnummer des zugeordneten Vario-Artikels (bei Varianten die Variantennummer). Leer = noch kein Mapping.',
      width: 170,
      minWidth: 170,
      cellStyle: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '11px' },
      cellClass: 'kl-ell-cell',
      tooltipField: 'effective_vario_article_number',
    },
    {
      field: 'ean',
      headerName: 'EAN',
      width: 140,
      cellStyle: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px' },
      cellClass: 'kl-ell-cell',
      tooltipField: 'ean',
    },
    { field: 'title', headerName: 'Titel', flex: 2, minWidth: 250, cellClass: 'kl-ell-cell', tooltipField: 'title' },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      cellRenderer: statusRenderer,
    },
    {
      colId: 'price',
      headerName: 'Preis',
      headerTooltip: 'Verkaufspreis (Vario-Kanal). Bei zugeordneten Angeboten editierbar → schreibt den Kanal-Preis + überträgt ihn an Otto. Orange = Übertragung läuft, bis Otto den neuen Preis übernommen hat.',
      width: 120,
      valueGetter: (p: ValueGetterParams<UnitRow>) => {
        if (!p.data) return null;
        const cents = p.data.channel_price_cents ?? p.data.price_cents;
        return cents == null ? null : cents / 100;
      },
      valueFormatter: priceFormatter,
      editable: (p) => p.data?.vario_article_id != null,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { min: 0, precision: 2 },
      valueSetter: makePriceSetter(ctx),
      cellClassRules: {
        'kl-cell-editable': (p) => p.data?.vario_article_id != null,
        // Orange „Übertragung läuft": Ziel-Kanal-Preis weicht vom aktuellen Otto-Preis ab (bis live).
        'otto-cell-stock-will-change': (p) =>
          !!p.data && p.data.channel_price_cents != null && p.data.channel_price_cents !== (p.data.price_cents ?? null),
      },
      cellStyle: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    },
    {
      field: 'stock',
      headerName: 'Bestand',
      headerTooltip: 'Aktueller Otto-Bestand. Hell-orange = wird beim nächsten Abgleich geändert (Vario-Bestand bzw. Min/Max wirkt sich aus).',
      width: 100,
      cellStyle: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
      cellClassRules: { 'otto-cell-stock-will-change': (p) => !!p.data && ottoStockNeedsSync(p.data) },
      tooltipValueGetter: (p) =>
        p.data && ottoStockNeedsSync(p.data)
          ? 'Bestand wird beim nächsten Abgleich an Otto angepasst.'
          : undefined,
    },
    {
      field: 'vario_stock',
      headerName: 'Vario-Bestand',
      headerTooltip: 'Aktuell verfügbarer Bestand aus Vario (stockAvailable). Wird nach jedem Sync aktualisiert.',
      width: 120,
      valueFormatter: varioStockFormatter,
      cellStyle: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    },
    {
      field: 'min_bestand',
      headerName: 'Min',
      headerTooltip:
        'Min-Bestand (optional). Trigger: sobald Otto-Bestand ≤ Min ODER Vario-Verfügbar < Min, wird auf Max aufgefüllt (gekappt durch Vario). Nur aktiv wenn beide Felder gesetzt sind. Leer lassen = Feature aus.',
      width: 70,
      editable: (p) => p.data?.vario_article_id != null,
      cellClassRules: { 'kl-cell-editable': (p) => p.data?.vario_article_id != null },
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { min: 0, precision: 0 },
      valueSetter: makeMinMaxSetter(ctx, 'min', () => {}),
      cellStyle: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    },
    {
      field: 'max_bestand',
      headerName: 'Max',
      headerTooltip:
        'Max-Bestand (optional). Zielwert beim Auffüllen. Nur aktiv wenn auch Min gesetzt ist. Leer lassen = Feature aus.',
      width: 70,
      editable: (p) => p.data?.vario_article_id != null,
      cellClassRules: { 'kl-cell-editable': (p) => p.data?.vario_article_id != null },
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { min: 0, precision: 0 },
      valueSetter: makeMinMaxSetter(ctx, 'max', () => {}),
      cellStyle: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    },
    {
      headerName: 'Aktionen',
      colId: 'actions',
      headerTooltip: 'Zuordnen · Otto-Produkt / Vario-Artikel öffnen',
      width: 130,
      pinned: 'right',
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: (p: ICellRendererParams<UnitRow>) => actionsRenderer(p, onAssign),
      cellStyle: { textAlign: 'center' },
    },
  ];

  const datasource: IDatasource = {
    getRows: async (params: IGetRowsParams) => {
      try {
        const { rows, total } = await loadUnitsPage(ctx, filters, params);
        let lastRow: number;
        if (total !== null) {
          lastRow = total;
        } else if (rows.length < params.endRow - params.startRow) {
          lastRow = params.startRow + rows.length;
        } else {
          lastRow = -1;
        }
        params.successCallback(rows, lastRow);
      } catch (e) {
        console.error('[offers] datasource.getRows failed', e);
        params.failCallback();
      }
    },
  };

  const options: GridOptions<UnitRow> = {
    columnDefs: cols,
    defaultColDef: { sortable: true, filter: false, resizable: true },
    // Mehrfach-Zeilenauswahl für Bulk-Min/Max. String-Form (kein object-Form) + per-Row-Checkbox.
    // suppressRowClickSelection: nur die Checkbox togglet die Auswahl, der Row-Klick öffnet den Drawer.
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    onSelectionChanged,
    stopEditingWhenCellsLoseFocus: true,
    enableCellTextSelection: true,
    // Native Browser-Tooltips (title-Attribut) statt der AG-Grid-Tooltip-Komponente: Die blendete
    // sich 2 s nach dem Scrollen unter der ruhenden Maus ein, änderte dabei die Höhe des
    // Grid-Körpers (bodyHeightChanged) und zeichnete alle sichtbaren Zeilen neu. Für den Betreiber
    // sah das aus wie ein Neuladen der Liste (16.10.2026, per Ereignis-Mitschnitt belegt).
    enableBrowserTooltips: true,
    rowModelType: 'infinite',
    cacheBlockSize: GRID_BLOCK_SIZE,
    maxBlocksInCache: GRID_CACHE_BLOCKS,
    infiniteInitialRowCount: 50,
    blockLoadDebounceMillis: 100,
    datasource,
    // Selektion ist page-lokal: bei Page-Wechsel/Page-Size-Change verwerfen, damit Bulk-Aktionen
    // nicht überraschend auf nicht mehr sichtbare Zeilen wirken.
    onPaginationChanged: (params) => {
      if (params.newPage || params.newPageSize) params.api.deselectAll();
    },
    // Klick auf eine nicht-Aktions-Zelle öffnet den read-only Detail-Drawer.
    onCellClicked: (e) => {
      const colId = e.column?.getColId?.() ?? '';
      // Auswahl-Checkbox-Spalte + Aktions-Spalte + AG-Grid-interne Controls-Spalte:
      // kein Drawer, deren Klick togglet/öffnet eigene Handler.
      if (colId === 'actions' || colId === 'kl-select' || colId.startsWith('ag-Grid-')) return;
      // Editierbare Zellen (Min/Max bei gemappten Zeilen) → Inline-Edit, nicht Drawer öffnen.
      // `editable` ist eine Funktion (immer truthy) → laufzeit-korrekt via isCellEditable prüfen,
      // damit ungemappte (nicht-editierbare) Min/Max-Klicks weiterhin den Drawer öffnen.
      if (e.node && e.column?.isCellEditable(e.node)) return;
      const target = e.event?.target as HTMLElement | null;
      if (target?.closest('button, a, input, select, label, [data-act]')) return;
      if (e.data) onRowOpen(e.data);
    },
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [25, 50, 100, 200],
    rowClassRules: {
      'kl-row-deleted': (p) => p.data?.deleted_at != null,
      // Hellblau: noch kein Mapping, aber Vario-Artikel existiert (via EAN/SKU gematched).
      'kl-row-vario-suggested': (p) =>
        p.data != null &&
        p.data.vario_article_id == null &&
        p.data.suggested_vario_article_id != null &&
        p.data.deleted_at == null,
      // Hellrot: noch keinem Vario-Artikel zugeordnet UND kein Auto-Vorschlag.
      'kl-row-vario-unmapped': (p) =>
        p.data != null &&
        p.data.vario_article_id == null &&
        p.data.suggested_vario_article_id == null &&
        p.data.deleted_at == null,
    },
    // Infinite Row Model emits loading-rows without `data` - guard against that.
    getRowId: (p) => (p.data ? `${p.data.storefront}::${p.data.sku}` : ''),
  };
  return createGrid(eGrid, options);
}

// ---------------------------------------------------------------------------
// Cell renderers / helpers
// ---------------------------------------------------------------------------

function varioStockFormatter(p: ValueFormatterParams<UnitRow>): string {
  // Kein Mapping → leer; gemappt aber noch kein Refresh → '…'; sonst die Zahl.
  if (isStocklessRow(p.data)) return 'kein Bestand';
  if (!p.data?.vario_article_id) return '';
  if (p.value === null || p.value === undefined) return '…';
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(p.value as number);
}

function statusRenderer(p: ICellRendererParams<UnitRow>) {
  if (p.data?.deleted_at) return `<span class="st st-inaktiv"><span class="pd"></span>Gelöscht</span>`;
  const status = (p.value as string | null) ?? 'unknown';
  const cls = status === 'active' ? 'st-aktiv' : status === 'processing' ? 'st-paus' : 'st-inaktiv';
  return `<span class="st ${cls}"><span class="pd"></span>${STATUS_LABEL[status] ?? status}</span>`;
}

/**
 * Actions-Renderer (read-only): Otto-Produktlink (falls `otto_product_link` gesetzt) +
 * Vario-Artikel-Link (falls gemappt). Kein „Zuordnen"-Button (zurückgestellt).
 */
function actionsRenderer(p: ICellRendererParams<UnitRow>, onAssign: (row: UnitRow) => void): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'act-cell';
  wrap.style.justifyContent = 'center';

  // 0) Zuordnen / Zuordnung bearbeiten - Otto-Angebot ↔ bestehender Vario-Artikel.
  if (p.data && !p.data.deleted_at) {
    const row = p.data;
    const mapped = !!row.vario_article_id;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'act-icon';
    b.title = mapped ? 'Vario-Zuordnung bearbeiten' : 'Vario-Artikel zuordnen';
    b.innerHTML = '<span class="v-icon fa-regular fa-link"></span>';
    b.addEventListener('click', (e) => { e.stopPropagation(); onAssign(row); });
    wrap.appendChild(b);
  }

  // 1) Otto-Produktlink - nur wenn gesetzt.
  if (p.data?.otto_product_link) {
    const a = document.createElement('a');
    a.className = 'act-icon';
    a.href = p.data.otto_product_link;
    a.target = '_blank';
    a.rel = 'noopener';
    a.title = 'Otto-Produkt öffnen';
    a.innerHTML = '<span class="v-icon fa-regular fa-bag-shopping"></span>';
    wrap.appendChild(a);
  } else {
    wrap.appendChild(makeDisabledIcon('fa-bag-shopping', 'Kein Otto-Produktlink verfügbar'));
  }

  // 2) Vario-VERKAUFSKANAL im neuen Tab öffnen - nur sichtbar wenn zugeordnet. Öffnet direkt das
  //    Kanal-Listing (channel_vario_article_id + channel_vario_listing_id, View 0274); fällt auf das
  //    Artikel-Dashboard zurück, falls die Listing-ID (noch) fehlt.
  if (p.data?.vario_article_id) {
    const channelUrl = varioListingUrl(p.data.channel_vario_article_id, p.data.channel_vario_listing_id);
    const varioUrl = channelUrl ?? varioArticleUrl(p.data.vario_article_id);
    if (varioUrl) {
      const label = p.data.effective_vario_article_number ?? p.data.vario_article_number ?? p.data.vario_article_id;
      const a = document.createElement('a');
      a.className = 'act-icon';
      a.href = varioUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      a.title = channelUrl ? `Verkaufskanal ${label} öffnen` : `Vario-Artikel ${label} öffnen`;
      a.innerHTML = '<span class="v-icon fa-regular fa-up-right-from-square"></span>';
      wrap.appendChild(a);
    }
  } else {
    wrap.appendChild(makeDisabledIcon('fa-up-right-from-square', 'Noch keinem Vario-Artikel zugeordnet'));
  }

  return wrap;
}

function makeDisabledIcon(faClass: string, tooltip: string): HTMLElement {
  const span = document.createElement('span');
  span.className = 'act-icon kl-link-placeholder';
  span.title = tooltip;
  span.innerHTML = `<span class="v-icon fa-regular ${faClass}" style="opacity:0.35;"></span>`;
  return span;
}

// p.value ist EUR (valueGetter liefert (channel_price_cents ?? price_cents)/100).
function priceFormatter(p: ValueFormatterParams<UnitRow>): string {
  const eur = p.value as number | null | undefined;
  if (eur === null || eur === undefined) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(eur);
}

/**
 * Setter für die Preis-Spalte (EUR). Schreibt den Vario-Kanal-Preis über die Edge-Fn
 * `otto-set-channel-price` (Preis-Hoheit = Vario-Verkaufskanal) und stößt - bei Lister-Listings —
 * den Otto-Produkt-Re-Push an (Otto hat kein separates Preis-Interface). Optimistisch: Ziel-Preis
 * (`channel_price_cents`) sofort setzen → Zelle zeigt den neuen Preis + wird orange (Übertragung
 * läuft), bis Otto ihn übernommen hat. Bei Fehler Revert.
 */
function makePriceSetter(ctx: OffersCtx) {
  return (params: ValueSetterParams<UnitRow>): boolean => {
    if (!params.data) return false;
    const row = params.data;
    if (params.newValue === null || params.newValue === undefined || params.newValue === '') return false;
    const eur = typeof params.newValue === 'number' ? params.newValue : Number(String(params.newValue).replace(',', '.'));
    if (!Number.isFinite(eur) || eur <= 0) { showToast('Preis muss größer als 0 sein.', 'error'); return false; }
    const newCents = Math.round(eur * 100);
    const oldTarget = row.channel_price_cents;
    const oldDisplay = row.channel_price_cents ?? row.price_cents ?? null;
    if (newCents === oldDisplay) return false;

    // Optimistisch: Ziel-Preis setzen → Zelle zeigt neuen Preis + wird orange (Übertragung läuft).
    row.channel_price_cents = newCents;
    if (params.node) params.api.refreshCells({ rowNodes: [params.node], force: true });

    void (async () => {
      try {
        const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/otto-set-channel-price`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
          body: JSON.stringify({ sku: row.sku, priceEur: eur }),
        });
        const b = (await res.json().catch(() => ({}))) as { error?: { message: string }; pushed?: boolean };
        if (!res.ok || b.error) throw new Error(b.error?.message ?? httpErrorText(res.status));
        showToast(b.pushed ? 'Preis gesetzt - wird zu Otto übertragen.' : 'Preis in Vario gesetzt (kein Lister-Listing → kein Otto-Push).', 'success');
      } catch (e) {
        row.channel_price_cents = oldTarget;
        if (params.node) params.api.refreshCells({ rowNodes: [params.node], force: true });
        showToast(`Preis konnte nicht gesetzt werden: ${(e as Error).message}`, 'error');
      }
    })();
    return true;
  };
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'gerade eben';
  const min = Math.round(sec / 60);
  if (min < 60) return `vor ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `vor ${h} h`;
  const days = Math.round(h / 24);
  if (days < 30) return `vor ${days} T`;
  return d.toLocaleDateString('de-DE');
}

// ---------------------------------------------------------------------------
// Data loaders
// ---------------------------------------------------------------------------

/** PostgREST column names that AG Grid is allowed to sort by (existing Otto-View columns). */
const SORTABLE_COLUMNS: Record<string, string> = {
  sku: 'sku',
  ean: 'ean',
  title: 'title',
  status: 'status',
  price_cents: 'price_cents',
  stock: 'stock',
  vario_stock: 'vario_stock',
  updated_at: 'updated_at',
};

interface PageRequest {
  startRow: number;
  endRow: number;
  sortModel: ReadonlyArray<{ colId: string; sort: 'asc' | 'desc' }>;
}

/**
 * WHERE-Predicates für `loadUnitsPage` (Grid-Datasource). Search-Filter inkludiert —
 * Mehrfach-Token mit AND-stacked OR-Conditions.
 */
function applyOffersFilters<T extends {
  eq: (col: string, val: unknown) => T;
  is: (col: string, val: unknown) => T;
  not: (col: string, op: string, val: unknown) => T;
  or: (cond: string) => T;
}>(q: T, filters: FilterState): T {
  // Getombstete (gelöschte) Angebote gehören NICHT in die normale Liste — sie erscheinen nur,
  // wenn explizit der „Gelöscht"-Status gewählt ist. Sonst immer `deleted_at is null` (auch in der
  // Default-Ansicht ohne Status-Filter). Sonst zeigt „Alle" auch bei Otto nicht mehr existierende
  // Angebote (z. B. vom refresh_units getombstete Seed-/Altbestände) mit leerer Vario-Spalte.
  if (filters.status === 'deleted') q = q.not('deleted_at', 'is', null);
  else if (filters.status) q = q.is('deleted_at', null).eq('status', filters.status);
  else q = q.is('deleted_at', null);
  if (filters.mapping === 'mapped') q = q.not('vario_article_id', 'is', null);
  else if (filters.mapping === 'unmapped') q = q.is('vario_article_id', null);
  else if (filters.mapping === 'exists-unmapped')
    q = q.is('vario_article_id', null).not('suggested_vario_article_id', 'is', null);

  if (filters.search.trim()) {
    // PostgREST .or() trennt Conditions per Komma - Sonderzeichen im User-Input escapen/normalisieren.
    const sanitized = filters.search
      .trim()
      .replace(/[%_]/g, (m) => `\\${m}`)
      .replace(/[,()\\]/g, ' ');
    const tokens = sanitized.split(/\s+/).filter((t) => t.length >= 2);
    for (const tok of tokens) {
      const term = `%${tok}%`;
      q = q.or(
        `ean.ilike.${term},title.ilike.${term},sku.ilike.${term},vario_article_number.ilike.${term},effective_vario_article_number.ilike.${term}`,
      );
    }
  }
  return q;
}

/**
 * Server-side page fetch for the Infinite Row Model. Uses PostgREST `.range(start, end-1)`
 * and `{ count: 'exact' }` so the grid can size the virtual scrollbar correctly.
 */
async function loadUnitsPage(
  ctx: OffersCtx,
  filters: FilterState,
  page: PageRequest,
): Promise<{ rows: UnitRow[]; total: number | null }> {
  let q = ctx.supabase
    .schema('otto')
    .from('units_with_mapping')
    .select(
      'storefront,sku,ean,title,status,price_cents,stock,otto_product_link,processing_status,otto_errors,otto_information,fetched_at,updated_at,deleted_at,vario_article_id,vario_article_number,effective_vario_article_number,channel_vario_article_id,channel_vario_listing_id,vario_stock,vario_stock_fetched_at,suggested_vario_article_id,suggested_vario_article_number,suggested_match_source,min_bestand,max_bestand,vario_product_type,channel_price_cents',
      { count: 'exact' },
    );
  q = applyOffersFilters(q, filters);

  // Apply AG Grid sort model (max 2 levels). Default beim Tab-Öffnen: SKU desc.
  const sorts = page.sortModel.length > 0 ? page.sortModel.slice(0, 2) : [{ colId: 'sku', sort: 'desc' as const }];
  let anySort = false;
  for (const s of sorts) {
    const col = SORTABLE_COLUMNS[s.colId];
    if (!col) continue;
    q = q.order(col, { ascending: s.sort === 'asc', nullsFirst: false });
    anySort = true;
  }
  if (!anySort) q = q.order('sku', { ascending: false });

  q = q.range(page.startRow, page.endRow - 1);

  const { data, error, count } = await q;
  if (error) {
    console.error('[offers] loadUnitsPage', error);
    throw new Error(error.message);
  }
  return { rows: (data ?? []) as UnitRow[], total: count ?? null };
}

function escapeHtml(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
